import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Science as TestIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import type {
  MarketingEmailCampaignDto,
  MarketingEmailComposePayload,
  MarketingEmailPreview,
  MarketingEmailRecipient,
} from '../services/marketingEmailTypes';
import { useAuth } from '../contexts/AuthContext';

const EMPTY_COMPOSE: MarketingEmailComposePayload = {
  subject: '',
  heading: '',
  body: '',
  ctaLabel: '',
  ctaUrl: '',
};

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

const AdminEmailing: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const [recipientPage, setRecipientPage] = useState(0);
  const [recipientRowsPerPage, setRecipientRowsPerPage] = useState(25);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]); // recipient selection

  const [compose, setCompose] = useState<MarketingEmailComposePayload>(EMPTY_COMPOSE);
  const debouncedCompose = useDebouncedValue(compose, 400);

  const [campaignPage, setCampaignPage] = useState(0);
  const [campaignRowsPerPage, setCampaignRowsPerPage] = useState(20);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);

  const recipientsQuery = useQuery({
    queryKey: [
      'admin-emailing-recipients',
      debouncedSearch,
      recipientPage,
      recipientRowsPerPage,
    ],
    queryFn: () =>
      apiService.getMarketingEmailRecipients({
        q: debouncedSearch || undefined,
        page: recipientPage + 1,
        limit: recipientRowsPerPage,
        activeOnly: false,
      }),
    enabled: tab === 0,
  });

  const previewQuery = useQuery({
    queryKey: ['admin-emailing-preview', debouncedCompose],
    queryFn: () => apiService.previewMarketingEmail(debouncedCompose),
    enabled:
      tab === 0 &&
      debouncedCompose.subject.trim().length > 0 &&
      debouncedCompose.heading.trim().length > 0 &&
      debouncedCompose.body.trim().length > 0,
    retry: false,
  });

  const campaignsQuery = useQuery({
    queryKey: ['admin-emailing-campaigns', campaignPage, campaignRowsPerPage],
    queryFn: () =>
      apiService.getMarketingEmailCampaigns({
        page: campaignPage + 1,
        limit: campaignRowsPerPage,
      }),
    enabled: tab === 1,
  });

  const recipients = useMemo(
    () => recipientsQuery.data?.users ?? [],
    [recipientsQuery.data?.users],
  );
  const recipientPagination = recipientsQuery.data?.pagination;

  const selectableOnPage = useMemo(
    () => recipients.filter((u) => u.isActive),
    [recipients],
  );

  const allOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((u) => selectedUserIds.includes(u._id));

  const someOnPageSelected =
    selectableOnPage.some((u) => selectedUserIds.includes(u._id)) && !allOnPageSelected;

  const toggleUser = useCallback((userId: string, isActive: boolean) => {
    if (!isActive) return;
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId],
    );
  }, []);

  const toggleAllOnPage = useCallback(() => {
    setSelectedUserIds((prev) => {
      if (allOnPageSelected) {
        const pageIds = selectableOnPage.map((u) => u._id);
        return prev.filter((id) => !pageIds.includes(id));
      }
      const merged = [...prev];
      for (const u of selectableOnPage) {
        if (!merged.includes(u._id)) merged.push(u._id);
      }
      return merged;
    });
  }, [allOnPageSelected, selectableOnPage]);

  const composePayload = useMemo((): MarketingEmailComposePayload => {
    const payload: MarketingEmailComposePayload = {
      subject: compose.subject.trim(),
      heading: compose.heading.trim(),
      body: compose.body.trim(),
    };
    const ctaLabel = compose.ctaLabel?.trim();
    const ctaUrl = compose.ctaUrl?.trim();
    if (ctaLabel && ctaUrl) {
      payload.ctaLabel = ctaLabel;
      payload.ctaUrl = ctaUrl;
    }
    return payload;
  }, [compose]);

  const composeValid =
    composePayload.subject.length > 0 &&
    composePayload.heading.length > 0 &&
    composePayload.body.length > 0;

  const testSendMutation = useMutation({
    mutationFn: () => apiService.sendMarketingEmailTest(composePayload),
    onSuccess: (data) => {
      setFeedback({
        severity: 'success',
        message: `Correo de prueba enviado a ${data.to}`,
      });
    },
    onError: (e: any) => {
      setFeedback({
        severity: 'error',
        message:
          e?.response?.data?.message || e?.message || 'Error al enviar prueba',
      });
    },
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      const userIds = selectedUserIds.slice();
      return apiService.sendMarketingEmailCampaign({
        ...composePayload,
        userIds,
        confirm: true,
      });
    },
    onSuccess: (data) => {
      setConfirmOpen(false);
      setSelectedUserIds([]);
      setFeedback({
        severity: 'success',
        message: `Campaña enviada: ${data.campaign.sentCount} enviados, ${data.campaign.failedCount} fallidos`,
      });
      void queryClient.invalidateQueries({ queryKey: ['admin-emailing-campaigns'] });
      setTab(1);
    },
    onError: (e: any) => {
      setFeedback({
        severity: 'error',
        message:
          e?.response?.data?.message || e?.message || 'Error al enviar campaña',
      });
    },
  });

  const preview: MarketingEmailPreview | undefined = previewQuery.data;
  const previewError =
    previewQuery.error &&
    ((previewQuery.error as any)?.response?.data?.message ||
      (previewQuery.error as Error)?.message);

  const statusChip = (status: MarketingEmailCampaignDto['status']) => {
    if (status === 'completed') {
      return <Chip label="Completada" size="small" color="success" />;
    }
    if (status === 'failed') {
      return <Chip label="Fallida" size="small" color="error" />;
    }
    return <Chip label="Enviando" size="small" color="warning" />;
  };

  return (
    <Container maxWidth={false} sx={{ py: 4, maxWidth: 1400 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <EmailIcon color="primary" />
        <Typography variant="h1">Emailing</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Envía correos de marketing a usuarios seleccionados. Usa el envío de
        prueba antes de confirmar una campaña.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="Enviar" />
        <Tab label="Historial" />
      </Tabs>

      {tab === 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Paper sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="h6">Destinatarios</Typography>
              <Chip
                label={`${selectedUserIds.length} seleccionados`}
                size="small"
                color={selectedUserIds.length > 0 ? 'primary' : 'default'}
              />
            </Stack>
            <TextField
              fullWidth
              size="small"
              label="Buscar por nombre, email o empresa"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setRecipientPage(0);
              }}
              sx={{ mb: 2 }}
            />
            {recipientsQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : recipientsQuery.error ? (
              <Alert severity="error">Error al cargar usuarios</Alert>
            ) : (
              <>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={someOnPageSelected}
                            checked={allOnPageSelected}
                            onChange={toggleAllOnPage}
                            disabled={selectableOnPage.length === 0}
                          />
                        </TableCell>
                        <TableCell>Nombre</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Empresa</TableCell>
                        <TableCell>Rol</TableCell>
                        <TableCell>Estado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {recipients.map((u: MarketingEmailRecipient) => (
                        <TableRow
                          key={u._id}
                          hover={u.isActive}
                          sx={{ opacity: u.isActive ? 1 : 0.5 }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedUserIds.includes(u._id)}
                              onChange={() => toggleUser(u._id, u.isActive)}
                              disabled={!u.isActive}
                            />
                          </TableCell>
                          <TableCell>{u.name}</TableCell>
                          <TableCell>{u.email}</TableCell>
                          <TableCell>{u.company ?? '—'}</TableCell>
                          <TableCell>{u.role}</TableCell>
                          <TableCell>
                            {u.isActive ? (
                              <Chip label="Activo" size="small" color="success" variant="outlined" />
                            ) : (
                              <Chip label="Inactivo" size="small" variant="outlined" />
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {recipients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            Sin resultados
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={recipientPagination?.total ?? 0}
                  page={recipientPage}
                  onPageChange={(_, p) => setRecipientPage(p)}
                  rowsPerPage={recipientRowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRecipientRowsPerPage(parseInt(e.target.value, 10));
                    setRecipientPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  labelRowsPerPage="Filas"
                />
              </>
            )}
          </Paper>

          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Componer correo
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Asunto"
                  fullWidth
                  size="small"
                  value={compose.subject}
                  onChange={(e) =>
                    setCompose((c) => ({ ...c, subject: e.target.value }))
                  }
                />
                <TextField
                  label="Título"
                  fullWidth
                  size="small"
                  value={compose.heading}
                  onChange={(e) =>
                    setCompose((c) => ({ ...c, heading: e.target.value }))
                  }
                />
                <TextField
                  label="Cuerpo"
                  fullWidth
                  multiline
                  minRows={6}
                  value={compose.body}
                  onChange={(e) =>
                    setCompose((c) => ({ ...c, body: e.target.value }))
                  }
                  helperText="Un párrafo por línea"
                />
                <Divider />
                <Typography variant="subtitle2" color="text.secondary">
                  Botón CTA (opcional)
                </Typography>
                <TextField
                  label="Texto del botón"
                  fullWidth
                  size="small"
                  value={compose.ctaLabel ?? ''}
                  onChange={(e) =>
                    setCompose((c) => ({ ...c, ctaLabel: e.target.value }))
                  }
                />
                <TextField
                  label="URL del botón"
                  fullWidth
                  size="small"
                  value={compose.ctaUrl ?? ''}
                  onChange={(e) =>
                    setCompose((c) => ({ ...c, ctaUrl: e.target.value }))
                  }
                />
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap">
                <Button
                  variant="outlined"
                  startIcon={<TestIcon />}
                  disabled={!composeValid || testSendMutation.isPending}
                  onClick={() => testSendMutation.mutate()}
                >
                  {testSendMutation.isPending ? 'Enviando…' : 'Enviar prueba'}
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  disabled={
                    !composeValid ||
                    selectedUserIds.length === 0 ||
                    sendMutation.isPending
                  }
                  onClick={() => setConfirmOpen(true)}
                >
                  Enviar a {selectedUserIds.length} destinatario
                  {selectedUserIds.length === 1 ? '' : 's'}
                </Button>
              </Stack>
              {user?.email && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  La prueba se envía a {user.email}
                </Typography>
              )}
            </Paper>

            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Vista previa
              </Typography>
              {!composeValid ? (
                <Typography variant="body2" color="text.secondary">
                  Completa asunto, título y cuerpo para ver la vista previa.
                </Typography>
              ) : previewQuery.isFetching ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : previewError ? (
                <Alert severity="error">{previewError}</Alert>
              ) : preview?.html ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    maxHeight: 480,
                    overflow: 'auto',
                    bgcolor: 'background.default',
                  }}
                  dangerouslySetInnerHTML={{ __html: preview.html }}
                />
              ) : null}
            </Paper>
          </Stack>
        </Box>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          {campaignsQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : campaignsQuery.error ? (
            <Alert severity="error">Error al cargar historial</Alert>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Las aperturas son aproximadas (píxel de seguimiento). Los clics son más fiables.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Asunto</TableCell>
                      <TableCell>Título</TableCell>
                      <TableCell>Destinatarios</TableCell>
                      <TableCell>Enviados</TableCell>
                      <TableCell>Aperturas</TableCell>
                      <TableCell>Clics</TableCell>
                      <TableCell>Fallidos</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Enviado por</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(campaignsQuery.data?.campaigns ?? []).map(
                      (c: MarketingEmailCampaignDto) => (
                        <TableRow key={c._id}>
                          <TableCell>
                            {new Date(c.createdAt).toLocaleString('es-CL')}
                          </TableCell>
                          <TableCell>{c.subject}</TableCell>
                          <TableCell>{c.heading}</TableCell>
                          <TableCell>{c.recipientCount}</TableCell>
                          <TableCell>{c.sentCount}</TableCell>
                          <TableCell>
                            {c.openedCount}
                            {c.sentCount > 0
                              ? ` (${Math.round((c.openedCount / c.sentCount) * 100)}%)`
                              : ''}
                          </TableCell>
                          <TableCell>
                            {c.clickedCount}
                            {c.sentCount > 0
                              ? ` (${Math.round((c.clickedCount / c.sentCount) * 100)}%)`
                              : ''}
                          </TableCell>
                          <TableCell>{c.failedCount}</TableCell>
                          <TableCell>{statusChip(c.status)}</TableCell>
                          <TableCell>{c.createdByEmail}</TableCell>
                        </TableRow>
                      ),
                    )}
                    {(campaignsQuery.data?.campaigns ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          Sin campañas enviadas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={campaignsQuery.data?.pagination.total ?? 0}
                page={campaignPage}
                onPageChange={(_, p) => setCampaignPage(p)}
                rowsPerPage={campaignRowsPerPage}
                onRowsPerPageChange={(e) => {
                  setCampaignRowsPerPage(parseInt(e.target.value, 10));
                  setCampaignPage(0);
                }}
                rowsPerPageOptions={[10, 20, 50]}
                labelRowsPerPage="Filas"
              />
            </>
          )}
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar envío</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Vas a enviar el correo <strong>{composePayload.subject}</strong> a{' '}
            <strong>{selectedUserIds.length}</strong> usuario
            {selectedUserIds.length === 1 ? '' : 's'} activo
            {selectedUserIds.length === 1 ? '' : 's'}.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Esta acción no se puede deshacer. Revisa la vista previa y el envío
            de prueba antes de continuar.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={sendMutation.isPending}
            onClick={() => sendMutation.mutate()}
          >
            {sendMutation.isPending ? 'Enviando…' : 'Confirmar envío'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminEmailing;
